import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Plus,
  Search,
  Store,
  Package,
  AlertCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Loader2,
  CheckCircle,
  Pencil,
  Trash2,
  Ban,
  RotateCcw,
} from "lucide-react";
import PurchaseConfirmDialog from "./PurchaseConfirmDialog";
import {
  API,
  STATUS_STYLE,
  PAYMENT_STYLE,
  num,
  fmtMoney,
  fmtDate,
  purchaseNumber,
  errMsg,
  itemName,
  itemSku,
  itemImage,
  isManualItem,
  balanceDue,
  stockSummary,
} from "./Purchaseshared";

const TABS = ["All", "Pending", "Received", "Cancelled"];
const DATE_FILTERS = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 3 months", days: 90 },
  { label: "Last 12 months", days: 365 },
  { label: "All time", days: null },
];
const PAYMENT_FILTERS = ["All payments", "Unpaid", "Partially Paid", "Paid"];
const PER_PAGE = 10;
const SHOWN_ITEMS = 3;

const cardShadow = "shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.10)]";

/* ------------------------------------------------------------------ */
/*  small pieces                                                       */
/* ------------------------------------------------------------------ */
const Thumb = ({ item }) => {
  const [failed, setFailed] = useState(false);
  const src = itemImage(item);
  return (
    <div className="w-[72px] h-[72px] shrink-0 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
      {src && !failed ? (
        <img src={src} alt={itemName(item)} onError={() => setFailed(true)} className="w-full h-full object-cover" />
      ) : (
        <Package className="text-slate-300" size={26} />
      )}
    </div>
  );
};

const HeaderField = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <div className="text-sm text-slate-800 mt-0.5">{children}</div>
  </div>
);

const StatCard = ({ label, value, sub, tone }) => (
  <div className={`bg-white rounded-xl border border-slate-200 px-5 py-4 ${cardShadow}`}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${tone || "text-white"}`}>{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const CardSkeleton = () => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
    <div className="h-[72px] bg-slate-100" />
    <div className="p-5 flex gap-4">
      <div className="w-[72px] h-[72px] bg-slate-100 rounded-lg" />
      <div className="flex-1 space-y-3">
        <div className="h-4 w-1/3 bg-slate-100 rounded" />
        <div className="h-3 w-2/3 bg-slate-100 rounded" />
      </div>
    </div>
  </div>
);

const headline = (p) => {
  if (p.status === "Received") return `Received on ${fmtDate(p.receivedAt || p.updatedAt)}`;
  if (p.status === "Cancelled") return `Cancelled on ${fmtDate(p.updatedAt)}`;
  return "Awaiting delivery from vendor";
};

/* ------------------------------------------------------------------ */
/*  purchase card                                                      */
/* ------------------------------------------------------------------ */
const PurchaseCard = ({ purchase: p, onView, onEdit, onBill, billing, onAction }) => {
  const style = STATUS_STYLE[p.status] || STATUS_STYLE.Pending;
  const items = p.products || [];
  const extra = items.length - SHOWN_ITEMS;
  const due = balanceDue(p);

  return (
    <article className={`bg-white rounded-xl border border-slate-200 hover:shadow-[0_8px_24px_-12px_rgba(16,24,40,0.15)] transition-shadow ${cardShadow}`}>
      {/* header strip */}
      <header className="bg-slate-50 border-b border-slate-200 rounded-t-xl px-5 py-3.5 flex flex-wrap items-start gap-x-10 gap-y-3">
        <HeaderField label="Purchase placed">{fmtDate(p.purchaseDate || p.createdAt)}</HeaderField>
        <HeaderField label="Total">{fmtMoney(p.totalAmount)}</HeaderField>
        <HeaderField label="Vendor">
          <span className="text-blue-600">{p.supplier?.businessName || p.supplier?.fullName || p.supplierName || "Unknown vendor"}</span>
        </HeaderField>
        <HeaderField label="Payment">
          <span className="flex flex-wrap items-center gap-2">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${PAYMENT_STYLE[p.paymentStatus] || PAYMENT_STYLE.Unpaid}`}>
              {p.paymentStatus || "Unpaid"}
            </span>
            {p.status !== "Cancelled" && due > 0 && p.paymentStatus !== "Unpaid" && (
              <span className="text-xs text-slate-500">Due {fmtMoney(due)}</span>
            )}
          </span>
        </HeaderField>
        <div className="ml-auto text-right">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Purchase # {purchaseNumber(p)}</p>
          <button onClick={() => onView(p)} className="text-sm text-blue-600 hover:text-blue-700 hover:underline mt-0.5">
            View purchase details
          </button>
        </div>
      </header>

      {/* body */}
      <div className="p-5 grid grid-cols-1 lg:grid-cols-[1fr_230px] gap-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={`text-lg font-bold ${style.text}`}>{headline(p)}</h2>
            <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${style.pill}`}>{p.status}</span>
          </div>
          {p.status === "Received" && (
            <p className="flex items-center gap-1.5 text-sm text-emerald-700 mt-1">
              <CheckCircle size={14} /> Inventory updated
            </p>
          )}

          <ul className="mt-4 divide-y divide-slate-100">
            {items.slice(0, SHOWN_ITEMS).map((it, i) => (
              <li key={it._id || i} className="flex gap-4 py-3 first:pt-0 last:pb-0">
                <Thumb item={it} />
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-medium text-blue-700 line-clamp-2">{itemName(it)}</p>
                  <p className="text-[13px] text-slate-500 mt-1">
                    {itemSku(it) ? `SKU ${itemSku(it)} · ` : ""}Qty {it.quantity} × {fmtMoney(it.unitCost)}
                  </p>
                  {isManualItem(it) && (
                    <span className="inline-block mt-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                      Manual item
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {extra > 0 && (
            <button onClick={() => onView(p)} className="mt-3 text-sm text-blue-600 hover:underline">
              + {extra} more item{extra > 1 ? "s" : ""}
            </button>
          )}
        </div>

        {/* actions */}
        <aside className="flex flex-col gap-2.5 lg:border-l lg:border-slate-100 lg:pl-6">
          <button
            onClick={() => onView(p)}
            className="w-full rounded-lg py-2 text-sm font-medium text-white border border-transparent bg-[#b07d17] hover:bg-[#8c6312] shadow-sm transition-colors"
          >
            View details
          </button>
          <button
            onClick={() => onBill(p)}
            disabled={billing}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
          >
            {billing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            Download bill
          </button>
          {p.status === "Pending" && (
            <button
              onClick={() => onAction("receive", p)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
            >
              <CheckCircle size={15} /> Mark as received
            </button>
          )}
          {p.status === "Cancelled" && (
            <button
              onClick={() => onAction("reopen", p)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
            >
              <RotateCcw size={15} /> Reopen as pending
            </button>
          )}
          <button
            onClick={() => onEdit(p)}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
          >
            <Pencil size={15} /> Edit
          </button>
          <div className="flex items-center justify-center gap-4 pt-1 text-sm">
            {p.status !== "Cancelled" && (
              <button onClick={() => onAction("cancel", p)} className="inline-flex items-center gap-1 text-slate-500 hover:text-red-600">
                <Ban size={13} /> Cancel
              </button>
            )}
            <button onClick={() => onAction("delete", p)} className="inline-flex items-center gap-1 text-slate-500 hover:text-red-600">
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </aside>
      </div>
    </article>
  );
};

/* ------------------------------------------------------------------ */
/*  page                                                               */
/* ------------------------------------------------------------------ */
const AdminPurchaseList = () => {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS[3]);
  const [paymentFilter, setPaymentFilter] = useState(PAYMENT_FILTERS[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [billingId, setBillingId] = useState(null);
  const [pending, setPending] = useState(null); // { type, purchase }
  const [working, setWorking] = useState(false);

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/purchases/get-all`);
      setPurchases(data.purchases || []);
    } catch (err) {
      console.error(err);
      setError(errMsg(err, "We couldn't load the purchases. Check your connection and try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  /* ---------- filtering ---------- */
  const baseFiltered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const cutoff = dateFilter.days ? Date.now() - dateFilter.days * 86400000 : null;

    return purchases.filter((p) => {
      if (cutoff && new Date(p.purchaseDate || p.createdAt).getTime() < cutoff) return false;
      if (paymentFilter !== PAYMENT_FILTERS[0] && p.paymentStatus !== paymentFilter) return false;
      if (!q) return true;
      const hay = [
        p._id,
        purchaseNumber(p),
        p.supplierInvoiceNo,
        p.supplier?.businessName,
        p.supplier?.fullName,
        p.supplierName,
        ...(p.products || []).flatMap((it) => [itemName(it), itemSku(it)]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [purchases, searchTerm, dateFilter, paymentFilter]);

  const counts = useMemo(() => {
    const c = { All: baseFiltered.length, Pending: 0, Received: 0, Cancelled: 0 };
    baseFiltered.forEach((p) => {
      c[p.status] = (c[p.status] || 0) + 1;
    });
    return c;
  }, [baseFiltered]);

  const filtered = useMemo(
    () => (activeTab === "All" ? baseFiltered : baseFiltered.filter((p) => p.status === activeTab)),
    [baseFiltered, activeTab]
  );

  const stats = useMemo(() => {
    const live = purchases.filter((p) => p.status !== "Cancelled");
    return {
      spend: live.reduce((s, p) => s + num(p.totalAmount), 0),
      pending: purchases.filter((p) => p.status === "Pending").length,
      dues: live.reduce((s, p) => s + balanceDue(p), 0),
      received: purchases.filter((p) => p.status === "Received").length,
    };
  }, [purchases]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const startIndex = (currentPage - 1) * PER_PAGE;
  const pageItems = filtered.slice(startIndex, startIndex + PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageNumbers = useMemo(() => {
    const set = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const list = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    return list.reduce((acc, n, i) => {
      if (i && n - list[i - 1] > 1) acc.push("…");
      acc.push(n);
      return acc;
    }, []);
  }, [currentPage, totalPages]);

  /* ---------- actions ---------- */
  const handleBill = async (p) => {
    setBillingId(p._id);
    try {
      // loaded on demand so the PDF library isn't in the main bundle
      const { downloadPurchaseBill } = await import("./purchaseBill");
      downloadPurchaseBill(p);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't create the bill. Please try again.");
    } finally {
      setBillingId(null);
    }
  };

  const confirmCopy = useMemo(() => {
    if (!pending) return null;
    const { type, purchase: p } = pending;
    const s = stockSummary(p);
    const stockText = `${s.units} unit${s.units === 1 ? "" : "s"} across ${s.products} catalogue product${s.products === 1 ? "" : "s"}`;
    const manualText = s.manual ? ` ${s.manual} manual item${s.manual > 1 ? "s are" : " is"} not tracked in inventory.` : "";

    switch (type) {
      case "receive":
        return {
          title: "Mark as received?",
          message: `Purchase ${purchaseNumber(p)} from ${p.supplier?.businessName || p.supplier?.fullName || p.supplierName || "the vendor"} has arrived.`,
          details: `${stockText} will be added to inventory.${manualText}`,
          confirmLabel: "Mark as received",
          tone: "primary",
        };
      case "reopen":
        return {
          title: "Reopen this purchase?",
          message: `Purchase ${purchaseNumber(p)} will go back to Pending.`,
          details: "No stock change. You can mark it as received later.",
          confirmLabel: "Reopen",
          tone: "primary",
        };
      case "cancel":
        return {
          title: "Cancel this purchase?",
          message: `Purchase ${purchaseNumber(p)} will be marked as Cancelled.`,
          details:
            p.status === "Received"
              ? `The ${stockText} added by this purchase will be removed from inventory again. This fails if some of those units were already sold.`
              : "No stock was added, so inventory is not affected.",
          confirmLabel: "Cancel purchase",
          tone: "danger",
        };
      default:
        return {
          title: "Delete this purchase?",
          message: `Purchase ${purchaseNumber(p)} will be permanently deleted. This can't be undone.`,
          details:
            p.status === "Received"
              ? `The ${stockText} it added will be removed from inventory first. This fails if some of those units were already sold.`
              : "No inventory will change.",
          confirmLabel: "Delete purchase",
          tone: "danger",
        };
    }
  }, [pending]);

  const runAction = async () => {
    const { type, purchase: p } = pending;
    setWorking(true);
    try {
      if (type === "delete") {
        await axios.delete(`${API}/purchases/${p._id}`);
        toast.success("Purchase deleted");
      } else {
        const status = { receive: "Received", reopen: "Pending", cancel: "Cancelled" }[type];
        await axios.put(`${API}/purchases/${p._id}/status`, { status });
        toast.success(`Purchase marked as ${status.toLowerCase()}`);
      }
      setPending(null);
      await fetchPurchases();
    } catch (err) {
      console.error(err);
      toast.error(errMsg(err, "That didn't work. Please try again."));
      setPending(null);
    } finally {
      setWorking(false);
    }
  };

  /* ---------- render ---------- */
  return (
    <div className="min-h-screen bg-[#F3F6FB] p-4 md:p-6 font-sans">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
          <div>
            <h1 className="text-[28px] leading-8 font-extrabold text-white tracking-tight flex items-center gap-2.5">
              <Store className="text-[#c59a3f]" size={28} />
              Purchases
            </h1>
            <p className="text-sm text-slate-500 mt-1.5">
              {loading
                ? "Loading purchases…"
                : `${filtered.length} purchase${filtered.length === 1 ? "" : "s"} · vendor buying and warehouse stock`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={fetchPurchases}
              disabled={loading}
              aria-label="Refresh purchases"
              className="p-2.5 rounded-lg text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={() => navigate("/admin-purchase/create")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white border border-transparent bg-[#b07d17] hover:bg-[#8c6312] shadow-sm transition-colors"
            >
              <Plus size={18} /> Create purchase
            </button>
          </div>
        </div>

        {/* Stats */}
        {!loading && !error && purchases.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <StatCard label="Total purchased" value={fmtMoney(stats.spend)} sub="Excluding cancelled" />
            <StatCard label="Awaiting delivery" value={stats.pending} sub="Pending purchases" tone="text-amber-600" />
            <StatCard label="Received" value={stats.received} sub="Stock added" tone="text-emerald-600" />
            <StatCard label="Outstanding dues" value={fmtMoney(stats.dues)} sub="Owed to vendors" tone={stats.dues > 0 ? "text-red-600" : "text-white"} />
          </div>
        )}

        {/* Search + filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="flex flex-1 shadow-sm rounded-lg overflow-hidden border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-[#d4af37]/50">
            <input
              type="text"
              placeholder="Search by purchase #, vendor, vendor invoice, product or SKU"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="flex-1 px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none"
            />
            <button
              aria-label="Search purchases"
              className="px-5 bg-[#b07d17] border-l border-transparent text-white hover:bg-[#8c6312] transition-colors"
            >
              <Search size={18} />
            </button>
          </div>

          {[
            {
              label: "Payment",
              value: paymentFilter,
              options: PAYMENT_FILTERS,
              onChange: (v) => setPaymentFilter(v),
            },
            {
              label: "Placed in",
              value: dateFilter.label,
              options: DATE_FILTERS.map((d) => d.label),
              onChange: (v) => setDateFilter(DATE_FILTERS.find((d) => d.label === v)),
            },
          ].map((f) => (
            <div key={f.label} className="relative">
              <select
                value={f.value}
                aria-label={f.label}
                onChange={(e) => {
                  f.onChange(e.target.value);
                  setCurrentPage(1);
                }}
                className="appearance-none w-full md:w-auto bg-white border border-slate-300 rounded-lg pl-3 pr-9 py-2.5 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50"
              >
                {f.options.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-3 text-slate-500 pointer-events-none" />
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-5 overflow-x-auto">
          <div className="flex gap-1 min-w-max" role="tablist">
            {TABS.map((tab) => {
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-3 text-sm whitespace-nowrap border-b-[3px] -mb-px transition-colors ${active
                    ? "border-[#e47911] text-white font-semibold"
                    : "border-transparent text-slate-600 hover:text-white hover:border-slate-300"
                    }`}
                >
                  {tab}
                  <span className={`ml-1.5 text-xs ${active ? "text-[#c45500]" : "text-slate-400"}`}>{counts[tab] || 0}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-5 text-sm">
            <AlertCircle size={18} />
            <span className="flex-1">{error}</span>
            <button onClick={fetchPurchases} className="font-semibold underline">
              Try again
            </button>
          </div>
        )}

        {/* List */}
        <div className="space-y-5">
          {loading ? (
            [...Array(3)].map((_, i) => <CardSkeleton key={i} />)
          ) : pageItems.length > 0 ? (
            pageItems.map((p) => (
              <PurchaseCard
                key={p._id}
                purchase={p}
                billing={billingId === p._id}
                onView={(x) => navigate(`/admin-purchase/view/${x._id}`)}
                onEdit={(x) => navigate(`/admin-purchase/edit/${x._id}`)}
                onBill={handleBill}
                onAction={(type, purchase) => setPending({ type, purchase })}
              />
            ))
          ) : (
            !error && (
              <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
                <Package className="mx-auto text-slate-300" size={44} />
                <p className="mt-3 text-slate-800 font-semibold">
                  {purchases.length === 0 ? "No purchases yet" : "No purchases found"}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {purchases.length === 0
                    ? "Record your first purchase from a vendor."
                    : "Try a different status, payment filter, date range or search term."}
                </p>
                {purchases.length === 0 && (
                  <button
                    onClick={() => navigate("/admin-purchase/create")}
                    className="mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white border border-transparent bg-[#b07d17]"
                  >
                    <Plus size={16} /> Create purchase
                  </button>
                )}
              </div>
            )
          )}
        </div>

        {/* Pagination */}
        {!loading && filtered.length > PER_PAGE && (
          <nav className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3" aria-label="Pagination">
            <p className="text-sm text-slate-600">
              Showing {startIndex + 1}–{Math.min(startIndex + PER_PAGE, filtered.length)} of {filtered.length} purchases
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((n) => Math.max(1, n - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="p-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {pageNumbers.map((n, i) =>
                n === "…" ? (
                  <span key={`gap-${i}`} className="px-2 text-slate-400">
                    …
                  </span>
                ) : (
                  <button
                    key={n}
                    onClick={() => setCurrentPage(n)}
                    aria-current={currentPage === n ? "page" : undefined}
                    className={`min-w-[40px] px-3 py-2 rounded-lg text-sm ${currentPage === n ? "bg-[#b07d17] text-white font-semibold" : "border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                      }`}
                  >
                    {n}
                  </button>
                )
              )}
              <button
                onClick={() => setCurrentPage((n) => Math.min(totalPages, n + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className="p-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </nav>
        )}
      </div>

      <PurchaseConfirmDialog
        open={Boolean(pending)}
        {...(confirmCopy || {})}
        loading={working}
        onConfirm={runAction}
        onCancel={() => !working && setPending(null)}
      />
    </div>
  );
};

export default AdminPurchaseList;